<template>
  <div data-app>
    <div
      role="alert"
      v-bind:class="{ show: errors.length }"
      class="alert fade alert-danger"
    >
      <div class="alert-text" v-for="(error, i) in errors" :key="i">
        {{ error }}
      </div>
    </div>
    <v-card>
      <v-card-title>Firewall Rules</v-card-title>
      <v-card-text>
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="primary" dark v-bind="attrs" v-on="on" class="mr-2">
              <v-icon left>mdi-plus</v-icon>
              Add Search Filter
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="filter in availableSearchFilters"
              :key="filter.value"
              @click="addSearchFilter(filter.value)"
              :disabled="!!activeSearchFilters[filter.value]"
            >
              <v-list-item-title>{{ filter.text }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-chip-group>
          <v-chip
            v-for="(value, key) in activeSearchFilters"
            :key="key"
            close
            @click:close="removeSearchFilter(key)"
            :color="isFilterValid(key) ? 'black' : 'error'"
            :outlined="!isFilterValid(key)"
            class="ma-2"
          >
            <v-tooltip bottom :disabled="isFilterValid(key)">
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  {{ getFilterLabel(key) }}:
                  {{ getFilterDisplayValue(key, value) }}
                </span>
              </template>
              <span>{{ getFilterError(key) }}</span>
            </v-tooltip>
          </v-chip>
        </v-chip-group>
      </v-card-text>
      <v-data-table
        v-model="selected"
        :loading="loading"
        :headers="headers"
        :items="rules"
        :footer-props="{
          itemsPerPageOptions: [10, 20, 50, 100],
          showFirstLastPage: true,
          firstIcon: 'mdi-arrow-collapse-left',
          lastIcon: 'mdi-arrow-collapse-right',
          prevIcon: 'mdi-chevron-left',
          nextIcon: 'mdi-chevron-right',
        }"
        :items-per-page="itemsPerPage"
        :server-items-length="totalRules"
        @update:page="onPageChange"
        @update:sort-by="onSortChange"
        @update:sort-desc="onSortDescChange"
        @update:items-per-page="onItemsPerPageChange"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        item-key="id"
        class="elevation-1"
      >
        <template v-slot:item.source="{ item }">
          {{ getSource(item) }}
        </template>
        <template v-slot:item.filters="{ item }">
          <div v-if="item.filter_ids && item.filter_ids.length">
            <span v-for="(filterId, index) in item.filter_ids" :key="index">
              {{ filterId
              }}<span v-if="index < item.filter_ids.length - 1">, </span>
            </span>
          </div>
        </template>
        <template v-slot:item.propagated="{ item }">
          <span
            class="badge badge-pill"
            :class="item.propagated ? 'badge-success' : 'badge-warning'"
          >
            {{ item.propagated ? "Active" : "Deploying" }}
          </span>
        </template>
        <template v-slot:top>
          <v-toolbar flat color="white">
            <v-spacer></v-spacer>
            <v-dialog v-model="dialog" max-width="600px">
              <template v-slot:activator="{ on }">
                <v-btn color="primary" dark class="mb-2" v-on="on"
                  >New Rule</v-btn
                >
              </template>
              <v-card>
                <v-card-title>
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>

                <v-card-text>
                  <v-form ref="newForm" lazy-validation>
                    <v-container>
                      <v-row dense>
                        <v-col cols="12">
                          <v-text-field
                            v-model="editedItem.destination"
                            label="Destination*"
                            :rules="ipRules"
                            required
                          />
                        </v-col>
                        <v-col cols="4">
                          <v-select
                            :items="['Network', 'ASN', 'Country']"
                            v-model="editedItem.source_type"
                            label="Source Type"
                            editable
                          />
                        </v-col>
                        <v-col
                          cols="8"
                          v-if="editedItem.source_type === 'Network'"
                        >
                          <v-combobox
                            v-model="editedItem.source"
                            :items="sourceNetworkOptions"
                            label="Source Net*"
                            :rules="ipRules"
                            :disabled="editedItem.source_type !== 'Network'"
                          >
                            <template v-slot:item="{ item }">
                              {{ getSourceNetworkLabel(item) }}
                            </template>
                            <template v-slot:selection="{ item }">
                              {{ getSourceNetworkLabel(item) }}
                            </template>
                          </v-combobox>
                        </v-col>
                        <v-col cols="8" v-if="editedItem.source_type === 'ASN'">
                          <v-text-field
                            v-model="editedItem.source_asn"
                            label="Source ASN*"
                            :rules="asnNumber"
                            :disabled="editedItem.source_type !== 'ASN'"
                          />
                        </v-col>
                        <v-col
                          cols="8"
                          v-if="editedItem.source_type === 'Country'"
                        >
                          <v-autocomplete
                            :items="countryOptions"
                            v-model="editedItem.source_country"
                            label="Source Country*"
                            item-text="name"
                            item-value="code"
                            :disabled="editedItem.source_type !== 'Country'"
                          />
                        </v-col>
                        <v-col cols="12" sm="6" md="1">
                          <v-checkbox v-model="editedItem.protocol_any" />
                        </v-col>
                        <v-col cols="12" sm="6" md="11">
                          <v-select
                            v-model="editedItem.protocol"
                            :items="protocols"
                            label="Protocol"
                            editable
                            :disabled="!editedItem.protocol_any"
                          />
                        </v-col>
                        <v-col
                          v-if="
                            editedItem.protocol_any &&
                            hasPorts(editedItem.protocol)
                          "
                          cols="12"
                          sm="6"
                          md="1"
                        >
                          <v-checkbox v-model="editedItem.src_port_any" />
                        </v-col>
                        <v-col
                          v-if="
                            editedItem.protocol_any &&
                            hasPorts(editedItem.protocol)
                          "
                          cols="12"
                          sm="6"
                          md="5"
                        >
                          <v-text-field
                            v-model="editedItem.src_port"
                            type="number"
                            label="Src Port"
                            placeholder="Any"
                            persistent-placeholder
                            :rules="portNumber"
                            :disabled="!editedItem.src_port_any"
                          />
                        </v-col>
                        <v-col
                          v-if="
                            editedItem.protocol_any &&
                            hasPorts(editedItem.protocol)
                          "
                          cols="12"
                          sm="6"
                          md="1"
                        >
                          <v-checkbox v-model="editedItem.dst_port_any" />
                        </v-col>
                        <v-col
                          v-if="
                            editedItem.protocol_any &&
                            hasPorts(editedItem.protocol)
                          "
                          cols="12"
                          sm="6"
                          md="5"
                        >
                          <v-text-field
                            v-model="editedItem.dst_port"
                            type="number"
                            label="Dst Port"
                            placeholder="Any"
                            persistent-placeholder
                            :rules="portNumber"
                            :disabled="!editedItem.dst_port_any"
                          />
                        </v-col>
                        <v-col cols="4">
                          <v-select
                            :items="[
                              'block',
                              'whitelist',
                              'rate limit',
                              'filter',
                            ]"
                            v-model="editedItem.type"
                            label="Action Type*"
                            @change="onTypeChange"
                            required
                          />
                        </v-col>
                        <v-col
                          v-if="editedItem.type === 'rate limit'"
                          cols="12"
                        >
                          <v-select
                            v-model="editedItem.rate_limiter_id"
                            label="Rate limiter*"
                            :items="rate_limiters"
                            :rules="required"
                            required
                          />
                        </v-col>
                        <v-col cols="8" v-if="editedItem.type === 'filter'">
                          <v-select
                            v-model="selectedFilters"
                            :items="paginatedFilters"
                            label="Select Filters (up to 2)"
                            multiple
                            item-value="id"
                            :menu-props="{
                              closeOnContentClick: true,
                              eager: true,
                            }"
                            @change="onFilterChange"
                          >
                            <template v-slot:item="{ item, on, attrs }">
                              <v-list-item
                                v-bind="attrs"
                                v-on="on"
                                :class="{
                                  'highlighted-item': selectedFilters.includes(
                                    item.id
                                  ),
                                }"
                              >
                                <v-list-item-content>
                                  <v-list-item-title
                                    v-html="item.display_name"
                                  ></v-list-item-title>
                                  <v-list-item-subtitle
                                    v-if="item.settingsText"
                                    v-html="item.settingsText"
                                  ></v-list-item-subtitle>
                                </v-list-item-content>
                              </v-list-item>
                            </template>
                            <template v-slot:selection="{ item }">
                              <v-chip
                                v-if="selectedFilters.length"
                                :key="item.id"
                                close
                                @click.stop="removeFilter(item.id)"
                              >
                                {{ item.display_name }}
                              </v-chip>
                            </template>
                            <template v-slot:append-item>
                              <v-list-item>
                                <v-row justify="space-between">
                                  <v-btn
                                    icon
                                    @click="previousFilterPage"
                                    :disabled="filtersPage === 1"
                                  >
                                    <v-icon>mdi-chevron-left</v-icon>
                                  </v-btn>
                                  <span
                                    >Page {{ filtersPage }} of
                                    {{ totalFilterPages }}</span
                                  >
                                  <v-btn
                                    icon
                                    @click="nextFilterPage"
                                    :disabled="filtersPage === totalFilterPages"
                                  >
                                    <v-icon>mdi-chevron-right</v-icon>
                                  </v-btn>
                                </v-row>
                              </v-list-item>
                            </template>
                          </v-select>
                        </v-col>
                        <v-col cols="12">
                          <v-text-field
                            v-model="editedItem.comment"
                            label="Comment"
                          />
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-form>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue darken-1" text @click="closeDialog"
                    >Cancel</v-btn
                  >
                  <v-btn color="blue darken-1" text @click="saveRule"
                    >Save</v-btn
                  >
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon small @click="deleteItem(item)">mdi-delete</v-icon>
        </template>
      </v-data-table>
    </v-card>
    <filter-input
      v-if="activeFilterKey"
      :value="activeSearchFilters[activeFilterKey]"
      :label="getFilterLabel(activeFilterKey)"
      :validator="filterValidations[activeFilterKey]"
      :countryOptions="countryOptions"
      @input="updateFilterValue"
      @close="closeFilterInput"
    ></filter-input>
  </div>
</template>

<script>
import api from "@/common/api.service";
import ipProtocols from "@/common/config/protocols";
import Errors from "@/common/errors";
import ipAddr from "ip-address";
import { debounce } from "lodash";
import FilterInput from "@/components/FilterInput.vue";
import { overwrite, getNames, getCode } from "country-list";

// Overwrite country names for better display
overwrite([
  {
    code: "GB",
    name: "United Kingdom",
  },
  {
    code: "TW",
    name: "Taiwan",
  },
  {
    code: "VE",
    name: "Venezuela",
  },
  {
    code: "KP",
    name: "North Korea",
  },
  {
    code: "KR",
    name: "South Korea",
  },
  {
    code: "IR",
    name: "Iran",
  },
  {
    code: "BO",
    name: "Bolivia",
  },
  {
    code: "CD",
    name: "Congo",
  },
  {
    code: "FM",
    name: "Micronesia",
  },
  {
    code: "MD",
    name: "Moldova",
  },
  {
    code: "TZ",
    name: "Tanzania",
  },
]);

export default {
  mounted() {
    this.fetchRules();
  },

  data() {
    return {
      selected: [],
      headers: [
        { text: "ID", sortable: false, align: "left", value: "id" },
        { text: "Protocol", value: "protocol" },
        { text: "Destination", value: "destination" },
        { text: "Dst Port", value: "dst_port" },
        { text: "Source", value: "source" },
        { text: "Src Port", value: "src_port" },
        { text: "Filters", value: "filters" }, // Added Filters column
        { text: "Type", value: "type" },
        { text: "Comment", value: "comment" },
        { text: "Status", value: "propagated", sortable: false },
        { text: "Actions", value: "actions", sortable: false },
      ],
      rules: [],
      availableFilters: [], // To store fetched filters
      errors: [],
      rate_limiters: null,
      loading: true,
      dialog: false,
      editedItem: {
        destination: null,
        source_type: "Network",
        source: "0.0.0.0/0",
        source_asn: null,
        source_country: null,
        protocol_any: false,
        protocol: null,
        src_port_any: false,
        src_port: null,
        dst_port_any: false,
        dst_port: null,
        type: "block",
        rate_limiter_id: null,
        filter_ids: [],
        comment: null,
      },
      protocols: ipProtocols,
      selectedFilters: [],
      ipRules: [
        (v) => !!v || "IP address is required",
        (v) => (!!v && this.ipValidator(v)) || "IP address is invalid",
      ],
      countryOptions: this.getCountryOptions(),
      itemsPerPage: 10,
      totalRules: 0,
      currentPage: 1,
      totalFilters: 0,
      filtersPage: 1,
      filtersSize: 10, // Show 10 filters at a time
      totalFilterPages: 0,
      searchCounter: 0,
      availableSearchFilters: [
        { text: "Source", value: "source" },
        { text: "Destination", value: "destination" },
        { text: "Source ASN", value: "source_asn" },
        { text: "Source Country", value: "source_country" },
        { text: "Source Port", value: "src_port" },
        { text: "Destination Port", value: "dst_port" },
        { text: "Comment", value: "comment" },
      ],
      activeSearchFilters: {},
      filterValidations: {
        source: this.ipValidator,
        destination: this.ipValidator,
        source_asn: this.asnValidator,
        source_country: () => true, // Always valid
        src_port: this.portValidator,
        dst_port: this.portValidator,
        comment: () => true, // Always valid
      },
      searchMenu: false,
      activeFilterKey: null,
      sortBy: [],
      sortDesc: null,
    };
  },

  watch: {
    dialog(val) {
      if (!val) this.closeDialog();
    },

    "editedItem.destination"() {
      if (
        this.editedItem.source_type === "Network" &&
        this.isAnyIpNetwork(this.editedItem.source)
      ) {
        this.editedItem.source = this.anyIpNetwork;
      }
    },

    searchFilters: {
      handler() {
        this.debouncedFetchRules();
      },
      deep: true,
    },
  },

  computed: {
    formTitle() {
      return "New Rule";
    },

    anyIpNetwork() {
      return new ipAddr.Address4(this.editedItem.destination || "").isValid()
        ? "0.0.0.0/0"
        : "::/0";
    },

    sourceNetworkOptions() {
      return [this.anyIpNetwork];
    },

    paginatedFilters() {
      return this.availableFilters.map((filter) => {
        const settingsText = Object.entries(filter.settings || {})
          .map(([key, value]) => `${key}: ${value !== null ? value : "N/A"}`)
          .join(", ");

        const displayText =
          `${filter.id} - ${filter.name}` +
          (settingsText ? `<br><small>Settings: ${settingsText}</small>` : "");

        return {
          ...filter,
          display_name: displayText,
        };
      });
    },
  },

  components: {
    FilterInput,
  },

  methods: {
    getCountryOptions() {
      const countries = getNames().map((name) => ({
        name,
        code: getCode(name),
      }));
      // Sort countries by name
      return countries.sort((a, b) => a.name.localeCompare(b.name));
    },

    getSource({ source_asn, source, source_country }) {
      if (source_country) {
        // Find the country name by code
        const country = this.countryOptions.find(
          (c) => c.code === source_country
        );
        return country ? country.name : source_country;
      }
      return source_asn ? `AS${source_asn}` : source;
    },

    isAnyIpNetwork(source) {
      return source === "0.0.0.0/0" || source === "::/0";
    },

    getSourceNetworkLabel(source) {
      return this.isAnyIpNetwork(source) ? "Any IP Network" : source;
    },

    onFilterChange() {
      if (this.selectedFilters.length > 2) {
        this.selectedFilters = this.selectedFilters.slice(0, 2);
      }
    },

    removeFilter(item) {
      const index = this.selectedFilters.indexOf(item.id);
      if (index > -1) {
        this.selectedFilters.splice(index, 1);
      }
    },

    hasPorts(protocol) {
      return (
        protocol === "udp" ||
        protocol === "tcp" ||
        protocol === "dccp" ||
        protocol === "sctp"
      );
    },

    ipValidator(ip) {
      const ipv4 = new ipAddr.Address4(ip);
      if (ipv4.isValid()) {
        return ipv4.startAddress().correctForm() === ipv4.correctForm();
      }

      const ipv6 = new ipAddr.Address6(ip);
      if (ipv6.isValid()) {
        return ipv6.startAddress().correctForm() === ipv6.correctForm();
      }

      return false;
    },

    fetchRules() {
      this.searchCounter++;
      const currentRequest = this.searchCounter;

      this.loading = true;
      const queryParams = new URLSearchParams();
      queryParams.append("page", this.currentPage);
      queryParams.append("size", this.itemsPerPage);

      // Add sorting parameters if any
      if (this.sortBy.length > 0 && this.sortDesc != null) {
        const sortField = this.sortBy[0];
        const sortDirection = this.sortDesc ? "desc" : "asc";
        switch (sortField) {
          case "source":
            queryParams.append("sort", "source");
            queryParams.append("sort", "source_asn");
            queryParams.append("sort", "source_country");
            break;
          case "type":
            queryParams.append("sort", "whitelist");
            queryParams.append("sort", "rate_limiter_id");
            queryParams.append("sort", "filter_ids");
            break;
          case "filters":
            queryParams.append("sort", "filter_ids");
            break;
          default:
            queryParams.append("sort", sortField);
        }
        queryParams.append("order", sortDirection);
      }

      // Add non-empty search filters to the query
      Object.entries(this.activeSearchFilters).forEach(([key, value]) => {
        if (value && this.isFilterValid(key)) {
          queryParams.append(key, value);
        }
      });

      api
        .query(`v2/rules?${queryParams.toString()}`)
        .then((response) => {
          if (this.searchCounter == currentRequest) {
            this.rules = response.data.data.map((rule) => ({
              ...rule,
              type: rule.whitelist
                ? "whitelist"
                : rule.rate_limiter_id != null
                ? "rate limit"
                : Array.isArray(rule.filter_ids) && rule.filter_ids.length > 0
                ? "filter"
                : "block",
            }));
            this.totalRules = response.data.total;
          }
        })
        .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)))
        .finally(() => {
          this.loading = false;
        });
    },

    debouncedFetchRules: debounce(function () {
      this.currentPage = 1; // Reset to first page when search changes
      this.fetchRules();
    }, 300),

    fetchAvailableFilters() {
      this.loading = true;
      api
        .query(`v2/filters?page=${this.filtersPage}&size=${this.filtersSize}`)
        .then((response) => {
          this.availableFilters = response.data.items;
          this.totalFilters = response.data.total;
          this.totalFilterPages = Math.ceil(
            this.totalFilters / this.filtersSize
          );
        })
        .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)))
        .finally(() => {
          this.loading = false;
        });
    },

    nextFilterPage() {
      if (this.filtersPage < this.totalFilterPages) {
        this.filtersPage++;
        this.fetchAvailableFilters();
      }
    },

    previousFilterPage() {
      if (this.filtersPage > 1) {
        this.filtersPage--;
        this.fetchAvailableFilters();
      }
    },

    handleFilterMenu(isOpen) {
      if (isOpen) {
        this.fetchAvailableFilters();
      }
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchRules();
    },

    onItemsPerPageChange(size) {
      this.itemsPerPage = size;
      this.currentPage = 1; // Reset to the first page
      this.fetchRules();
    },

    onTypeChange(type) {
      if (type === "rate limit" && this.rate_limiters == null) {
        this.rate_limiters_loading = true;
        api
          .query("rate_limiters")
          .then(
            (response) =>
              (this.rate_limiters = response.data.rate_limiters.map((rl) => ({
                value: rl.id,
                text: `${rl.id} - ${rl.comment}`,
              })))
          )
          .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)))
          .finally(() => (this.rate_limiters_loading = false));
      }

      if (type === "filter") {
        this.fetchAvailableFilters();
        this.editedItem.whitelist = false;
      } else {
        this.editedItem.filter_ids = [];
        this.selectedFilters = [];
      }
    },

    closeDialog() {
      this.dialog = false;
      this.editedItem = {
        destination: "",
        source_type: "Network",
        source: "0.0.0.0/0",
        source_asn: null,
        source_country: null,
        protocol_any: false,
        protocol: null,
        src_port_any: false,
        src_port: null,
        dst_port_any: false,
        dst_port: null,
        type: "block",
        rate_limiter_id: null,
        filter_ids: [],
        comment: "",
      };
      this.selectedFilters = [];
    },

    saveRule() {
      if (!this.$refs.newForm.validate()) return;

      const newRule = {
        destination: this.editedItem.destination,
        whitelist: this.editedItem.type === "whitelist",
        priority: false,
      };

      switch (this.editedItem.source_type) {
        case "Network":
          newRule.source = this.editedItem.source;
          break;
        case "ASN":
          newRule.source_asn = this.editedItem.source_asn;
          break;
        case "Country":
          newRule.source_country = this.editedItem.source_country;
      }

      if (this.editedItem.protocol_any) {
        newRule.protocol = this.editedItem.protocol;
        if (this.hasPorts(this.editedItem.protocol)) {
          if (this.editedItem.src_port_any)
            newRule.src_port = this.editedItem.src_port;
          if (this.editedItem.dst_port_any)
            newRule.dst_port = this.editedItem.dst_port;
        }
      }

      if (this.editedItem.type === "rate limit")
        newRule.rate_limiter_id = this.editedItem.rate_limiter_id;
      if (this.editedItem.type === "filter") {
        newRule.filter_ids = this.selectedFilters;
      }

      if (!!this.editedItem.comment) newRule.comment = this.editedItem.comment;

      api
        .post("v2/rules", newRule)
        .then((_) => {
          this.fetchRules();
        })
        .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)))
        .finally(() => {
          this.closeDialog();
        });
    },

    deleteItem(item) {
      if (confirm("Are you sure you want to delete this item?")) {
        api.delete(`v2/rules/${item.id}`).then(() => {
          this.fetchRules();
        });
      }
    },

    addSearchFilter(filterKey) {
      // Check if the filter is already active
      if (!this.activeSearchFilters.hasOwnProperty(filterKey)) {
        // Initialize the filter value
        this.$set(this.activeSearchFilters, filterKey, "");
        this.activeFilterKey = filterKey;
        this.searchMenu = false;
      } else if (!this.isFilterValid(filterKey)) {
        // If the filter is invalid, remove it
        this.$delete(this.activeSearchFilters, filterKey);
        this.activeFilterKey = null;
        this.errors.push(`Removed invalid filter: ${filterKey}`);
      }
    },

    updateFilterValue(value) {
      if (this.activeFilterKey) {
        this.$set(this.activeSearchFilters, this.activeFilterKey, value);
        // Only call updateSearch if the filter is valid
        if (this.isFilterValid(this.activeFilterKey)) {
          this.updateSearch();
        }
      }
    },

    closeFilterInput() {
      if (this.activeFilterKey) {
        const currentValue = this.activeSearchFilters[this.activeFilterKey];
        if (!currentValue || !this.isFilterValid(this.activeFilterKey)) {
          this.$delete(this.activeSearchFilters, this.activeFilterKey);
        }
      }
      this.activeFilterKey = null;
    },

    confirmFilterAddition() {
      if (this.activeFilterKey && this.isFilterValid(this.activeFilterKey)) {
        // Only add the filter if it's valid
        this.$set(
          this.activeSearchFilters,
          this.activeFilterKey,
          this.activeSearchFilters[this.activeFilterKey]
        );
      } else {
        console.error(`Cannot add invalid filter: ${this.activeFilterKey}`);
      }
    },

    removeSearchFilter(key) {
      this.$delete(this.activeSearchFilters, key);
      this.updateSearch();

      // Reset activeFilterKey if the removed key is the active one
      if (this.activeFilterKey === key) {
        this.activeFilterKey = null;
      }
    },

    getFilterLabel(key) {
      const filter = this.availableSearchFilters.find((f) => f.value === key);
      return filter ? filter.text : key;
    },

    getFilterDisplayValue(key, value) {
      if (key === "source_country") {
        const country = this.countryOptions.find((c) => c.code === value);
        return country ? country.name : value;
      }
      return value;
    },

    updateSearch: debounce(function () {
      if (this.areAllFiltersValid()) {
        this.currentPage = 1;
        this.fetchRules();
      }
    }, 300),

    isFilterValid(key) {
      const value = this.activeSearchFilters[key];
      return !value || this.filterValidations[key](value);
    },

    areAllFiltersValid() {
      return Object.keys(this.activeSearchFilters).every(this.isFilterValid);
    },

    getFilterError(key) {
      if (this.isFilterValid(key)) return "";
      switch (key) {
        case "source":
        case "destination":
          return "Invalid IP address or CIDR notation";
        case "source_asn":
          return "Invalid ASN number";
        case "src_port":
        case "dst_port":
          return "Invalid port number";
        default:
          return "Invalid input";
      }
    },

    asnValidator(value) {
      if (!value) return true;
      const asn = parseInt(value, 10);
      return !isNaN(asn) && asn >= 1 && asn <= 4294967295;
    },

    portValidator(value) {
      if (!value) return true;
      const port = parseInt(value, 10);
      return !isNaN(port) && port >= 1 && port <= 65535;
    },

    toggleSearchMenu() {
      this.searchMenu = !this.searchMenu;
    },

    onSortDescChange(newSortDesc) {
      this.sortDesc = newSortDesc;
      this.fetchRules();
    },

    onSortChange(newSortBy) {
      this.sortBy = newSortBy;
      this.fetchRules();
    },
  },
};
</script>

<style scoped>
.v-chip {
  margin: 4px;
}
.error--text {
  color: #ff5252 !important;
}
</style>
